import { useCallback, useEffect, useMemo, useState } from "react";
import currency from "currency.js";
import { toaster } from "evergreen-ui";
import useControlHub from "../../hooks/useControlHub";
import useDebounce from "../../hooks/useDebounce";

const formatBillData = (data) => {
  const {
    status,
    invoice: {
      info: {
        invoice_date: date,
        amount,
        due_date: due,
        invoice_number: number,
        description,
      },
      files,
    },
    po_info: { po_amount, ...po },
    accounting_splits: splits,
    vendor,
    approvers,
    is_payable: isPayable,
    payment_info: paymentInfo,
  } = data;

  return {
    status,
    date,
    amount: currency(amount, { fromCents: true }),
    due,
    number,
    description,
    files,
    po: {
      ...po,
      amount: currency(po_amount, { fromCents: true }),
    },
    splits: splits.map((split) => ({
      ...split,
      amount: currency(split.amount, { fromCents: true }),
    })),
    vendor,
    approvers,
    isPayable,
    paymentInfo,
  };
};

const deformatBillDataChanges = (dataChanges) => {
  const data = Object.assign({}, ...dataChanges);
  const {
    date,
    amount,
    due,
    number,
    description,
    po,
    splits,
    vendor,
    approvers,
  } = data;
  const deformatted = {};
  const invoice = {
    amount: amount ? currency(amount).intValue : amount,
    invoice_date: date,
    due_date: due,
    invoice_number: number,
    description: description,
  };
  if (Object.values(invoice).some((_value) => _value)) {
    deformatted["invoice_info"] = invoice;
  }
  deformatted["vendor_id"] = vendor;
  if (po?.["biz_owner"]) {
    deformatted["biz_owner"] = po["biz_owner"];
  }
  if (splits) {
    deformatted["account_splits"] = splits.map((split) => {
      return {
        id: split.id || null,
        amount: currency(split.amount).intValue,
        categories: split["account_categories"].map((category) => ({
          account_category_id: category["category_id"],
          account_value_id: category["value_id"],
        })),
      };
    });
  }
  if (approvers && approvers.length) {
    deformatted["approvers"] = approvers.map((approver) => approver.id);
  }
  return deformatted;
};

const shouldSaveChange = (change) => {
  return Object.entries(change)
    .map(([changeKey, changeValue]) => {
      switch (changeKey) {
        case "splits":
          return changeValue
            .map((split) => split["account_categories"])
            .map((categories) =>
              categories.every((category) => category["value_id"])
            )
            .every((should) => should);
        case "po":
          return changeValue?.["biz_owner"];
        default:
          return changeValue;
      }
    })
    .every((should) => should);
};

const BILL_STATES = {
  load: {
    ready: "About to load bill data",
    processing: "Downloading bill data",
    success: "Successfully downloaded bill data",
    error: "Failed to download bill data",
  },
  save: {
    ready: "About to save bill data",
    processing: "Uploading bill data",
    success: "Successfully uploaded bill data",
    error: "Failed to upload bill data",
  },
};

const useBillForm = (billID) => {
  const { api } = useControlHub();
  const [bill, setBill] = useState();
  const [billState, setBillState] = useState(BILL_STATES.load.ready);
  const [dataChanges, setDataChanges] = useState([]);
  const debouncedDataChanges = useDebounce(dataChanges, 1500);

  const loadBill = useCallback(() => {
    setBillState(BILL_STATES.load.ready);
  }, []);

  const saveToBill = useCallback((value) => {
    if (!value || typeof value !== "object") {
      throw new Error("Invalid data");
    }
    setBill((prev) => ({ ...prev, ...value }));
    if (!shouldSaveChange(value)) {
      return;
    }
    setDataChanges((prev) => [...prev, value]);
  }, []);

  const getBill = useCallback(async () => {
    setBillState(BILL_STATES.load.processing);
    try {
      const data = await api.getBill({ billID });
      if (!data?.success) {
        throw new Error(data?.message || "Couldn't retrieve bill data");
      }
      setBillState(BILL_STATES.load.success);
      setBill({
        id: billID,
        ...formatBillData(data["bill_data"]),
      });
    } catch (error) {
      setBill();
      setBillState(BILL_STATES.load.error);
      toaster.danger(error.message);
    }
  }, [api, billID]);

  useEffect(() => {
    if (!debouncedDataChanges || !debouncedDataChanges.length) {
      return;
    }
    setBillState(BILL_STATES.save.ready);
  }, [debouncedDataChanges]);

  useEffect(() => {
    if (
      !api.isReady ||
      !billID ||
      billState !== BILL_STATES.load.ready ||
      dataChanges.length
    ) {
      return;
    }
    getBill();
  }, [api.isReady, billID, billState, dataChanges.length, getBill]);

  useEffect(() => {
    if (!api.isReady || billState !== BILL_STATES.save.ready) {
      return;
    }
    setBillState(BILL_STATES.save.processing);
    api
      .saveBill({
        billID,
        billData: deformatBillDataChanges(debouncedDataChanges),
      })
      .then((data) => {
        if (!data.success) {
          throw new Error("");
        }
        setBillState(BILL_STATES.save.success);
        setBill({
          id: billID,
          ...formatBillData(data["bill_data"]),
        });
        setDataChanges([]);
      })
      .catch(() => {
        setBillState(BILL_STATES.save.error);
      });
  }, [api, billID, billState, debouncedDataChanges]);

  const hook = useMemo(
    () => ({
      bill,
      billState,
      BILL_STATES,
      loadBill,
      saveToBill,
    }),
    [bill, billState, loadBill, saveToBill]
  );

  if (!billID) {
    throw new Error("No bill ID provided");
  }

  return hook;
};

export default useBillForm;
